import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AsyncPaginate } from "react-select-async-paginate";
import AddIcon from "@mui/icons-material/Add";
import CircleIcon from "@mui/icons-material/Circle";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import UploadImage from "../images/icons/upload-image.png";
import { useAddAds } from "../hooks/UseAddAds";
import { useWaits } from "../hooks/UseWait";
import Fetch from "../services/Fetch";
import { useConstants } from "../hooks/UseConstants";
import { buildAddAdsFormData } from "../helper/AddAdsFormData";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

function SectionCard({ icon, title, subtitle, children, theme }) {
    return (
        <Box
            sx={{
                p: 2.5,
                borderRadius: 3,
                border: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: "#eff6ff",
                        color: "#0D358C",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#0D358C" }}>
                        {title}
                    </Typography>
                    {subtitle ? (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
            {children}
        </Box>
    );
}

function AddAds({ onClickClose, setSnackbar, getBanners }) {
    const { language, host } = useConstants();
    const isAr = language === "ar";
    const theme = useTheme();
    const { sendWait, setSendWait } = useWaits();
    const {
        nameEn, setNameEn, nameAr, setNameAr, category, setCategory, categoryId, setCategoryId,
        image, setImage, isActive, setIsActive, activeFrom, setActiveFrom, activeUntil, setActiveUntil,
        descriptionEn, setDescriptionEn, descriptionAr, setDescriptionAr, majorId, setMajorId,
        academicDegreeId, setAcademicDegreeId,
    } = useAddAds();
    const [categoryValue, setCategoryValue] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [majors, setMajors] = useState([]);
    const [degrees, setDegrees] = useState([]);
    const intl = useIntl();
    const selectStyles = useMemo(() => ({
        control: (base) => ({
            ...base,
            minHeight: 40,
            borderRadius: 8,
            borderColor: "#cbd5e1",
        }),
        option: (provided) => ({
            ...provided,
            color: "black",
        }),
    }), []);

    const resetInputs = () => {
        setNameAr("");
        setNameEn("");
        setCategory("course");
        setCategoryId("");
        setCategoryValue(null);
        setIsActive("1");
        setActiveFrom("");
        setActiveUntil("");
        setDescriptionAr("");
        setDescriptionEn("");
        setImage("");
        setImagePreview("");
        setMajorId("");
        setAcademicDegreeId("");
    };

    const loadTargetingOptions = async () => {
        const majorsResult = await Fetch(host + "/majors?per_page=100", "GET", null);
        if (majorsResult.status === 200) {
            const payload = majorsResult.data?.data;
            setMajors(Array.isArray(payload) ? payload : (payload?.data || []));
        }

        const degreesResult = await Fetch(host + "/academic-degrees?per_page=100", "GET", null);
        if (degreesResult.status === 200) {
            const payload = degreesResult.data?.data;
            setDegrees(Array.isArray(payload) ? payload : (payload?.data || []));
        }
    };

    const loadCategories = async (searchQuery, _loadedOptions, { page, category: catType }) => {
        const response = await Fetch(
            host + `/${catType === "course" ? `courses?page=${page}&status[]=accepted` : `paths?page=${page}`}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ""}`,
            "GET",
            null
        );
        const items = response?.data?.data?.data || response?.data?.data || [];
        const pagination = response?.data?.pagination || response?.data?.data || {};
        const optionsFromApi = items.map((item) => ({
            value: item.id,
            label: isAr ? item.name_ar : item.name_en,
        }));
        return {
            options: optionsFromApi,
            hasMore: (pagination.current_page || 1) * (pagination.per_page || items.length || 1) < (pagination.total || items.length || 0),
            additional: { page: page + 1, category: catType },
        };
    };

    useEffect(() => {
        loadTargetingOptions();
        if (!isActive) {
            setIsActive("1");
        }
    }, []);

    useEffect(() => {
        setCategoryValue(null);
        setCategoryId("");
    }, [category, setCategoryId]);

    const addAds = async () => {
        if (!nameAr.trim() || !nameEn.trim()) {
            setSnackbar("error", isAr ? "أدخل عنوان الإعلان بالعربية والإنجليزية" : "Enter ad title in Arabic and English");
            return;
        }
        if (!categoryId) {
            setSnackbar("error", isAr ? "اختر الدورة أو المسار المرتبط" : "Select linked course or path");
            return;
        }
        if (!image) {
            setSnackbar("error", isAr ? "أضف صورة للإعلان — تظهر في كاروسيل الصفحة الرئيسية للطالب" : "Add a banner image for the student homepage carousel");
            return;
        }

        setSendWait(true);

        const formData = buildAddAdsFormData({
            activeFrom,
            activeUntil,
            category,
            categoryId,
            descriptionAr,
            descriptionEn,
            image,
            isActive,
            nameAr,
            nameEn,
            majorId,
            academicDegreeId,
        });

        const result = await Fetch(host + "/admin/banners/store", "POST", formData);

        if (result.status === 200) {
            setSnackbar("success", isAr ? "تم إضافة الإعلان بنجاح — سيظهر للطلاب في الصفحة الرئيسية" : "Ad added successfully — it will appear on the student homepage");
            await getBanners();
            resetInputs();
            onClickClose();
        } else if (result.status === 422) {
            setSnackbar("error", result.data.errors?.[0] || result.data.message);
        }

        setSendWait(false);
    };

    const fieldSx = { width: "100%" };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "92%", sm: "85%", md: "72%", lg: "62%" },
                maxWidth: 920,
                maxHeight: "92vh",
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(13,53,140,0.22)",
                display: "flex",
                flexDirection: "column",
            }}
            dir={isAr ? "rtl" : "ltr"}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CampaignOutlinedIcon sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800}>
                            <FormattedMessage id="add_ads" />
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.88 }}>
                            {isAr ? "يُعرض للطلاب في كاروسيل الصفحة الرئيسية" : "Shown to students on the homepage carousel"}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={() => { resetInputs(); onClickClose(); }}
                    sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, overflowY: "auto", flex: 1 }} className="none-view-scroll">
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <SectionCard
                        icon={<CampaignOutlinedIcon />}
                        title={isAr ? "معلومات الإعلان" : "Ad information"}
                        subtitle={isAr ? "العنوان والوصف بالعربية والإنجليزية" : "Title and description in Arabic and English"}
                        theme={theme}
                    >
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <TextField
                                sx={fieldSx}
                                variant="outlined"
                                size="small"
                                label={<FormattedMessage id="ads_title_ar" />}
                                onChange={(e) => setNameAr(e.target.value)}
                                value={nameAr}
                                required
                            />
                            <TextField
                                sx={fieldSx}
                                variant="outlined"
                                size="small"
                                label={<FormattedMessage id="ads_title_en" />}
                                onChange={(e) => setNameEn(e.target.value)}
                                value={nameEn}
                                required
                            />
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 2 }}>
                            <TextField
                                sx={fieldSx}
                                variant="outlined"
                                size="small"
                                multiline
                                minRows={3}
                                label={<FormattedMessage id="ads_description_ar" />}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                value={descriptionAr}
                            />
                            <TextField
                                sx={fieldSx}
                                variant="outlined"
                                size="small"
                                multiline
                                minRows={3}
                                label={<FormattedMessage id="ads_description_en" />}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                value={descriptionEn}
                            />
                        </Box>
                    </SectionCard>

                    <SectionCard
                        icon={<LinkOutlinedIcon />}
                        title={isAr ? "الربط والاستهداف" : "Link & targeting"}
                        subtitle={isAr ? "اربط الإعلان بدورة أو مسار وحدد الجمهور" : "Link to a course or path and target audience"}
                        theme={theme}
                    >
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="ads-type-label"><FormattedMessage id="ads_type" /></InputLabel>
                                <Select
                                    labelId="ads-type-label"
                                    value={category}
                                    label={<FormattedMessage id="ads_type" />}
                                    onChange={(e) => setCategory(e.target.value)}
                                >
                                    <MenuItem value="course"><FormattedMessage id="courses" /></MenuItem>
                                    <MenuItem value="path"><FormattedMessage id="paths" /></MenuItem>
                                </Select>
                            </FormControl>
                            <Box>
                                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.75, display: "block" }}>
                                    <FormattedMessage id="category" /> / <FormattedMessage id="course" />
                                </Typography>
                                <AsyncPaginate
                                    key={category}
                                    value={categoryValue}
                                    loadOptions={(searchQuery, loadedOptions, additional) =>
                                        loadCategories(searchQuery, loadedOptions, { ...additional, category })
                                    }
                                    additional={{ page: 1, category }}
                                    onChange={(option) => {
                                        setCategoryValue(option);
                                        setCategoryId(option?.value || "");
                                    }}
                                    placeholder={`${intl.formatMessage({ id: "category" })} / ${intl.formatMessage({ id: "course" })}`}
                                    styles={selectStyles}
                                    isSearchable
                                />
                            </Box>
                            <FormControl fullWidth size="small">
                                <InputLabel id="major-label"><FormattedMessage id="majors" /></InputLabel>
                                <Select
                                    labelId="major-label"
                                    value={majorId}
                                    label={<FormattedMessage id="majors" />}
                                    onChange={(e) => setMajorId(e.target.value)}
                                >
                                    <MenuItem value="">{isAr ? "كل التخصصات" : "All majors"}</MenuItem>
                                    {majors.map((item) => (
                                        <MenuItem key={item.id} value={item.id}>
                                            {isAr ? item.name_ar : item.name_en}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel id="degree-label"><FormattedMessage id="academic_degrees" /></InputLabel>
                                <Select
                                    labelId="degree-label"
                                    value={academicDegreeId}
                                    label={<FormattedMessage id="academic_degrees" />}
                                    onChange={(e) => setAcademicDegreeId(e.target.value)}
                                >
                                    <MenuItem value="">{isAr ? "كل المراحل" : "All degrees"}</MenuItem>
                                    {degrees.map((item) => (
                                        <MenuItem key={item.id} value={item.id}>
                                            {isAr ? item.name_ar : item.name_en}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </SectionCard>

                    <SectionCard
                        icon={<ScheduleOutlinedIcon />}
                        title={isAr ? "الجدولة والحالة" : "Schedule & status"}
                        subtitle={isAr ? "متى يظهر الإعلان للطلاب" : "When the ad is visible to students"}
                        theme={theme}
                    >
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                            <TextField
                                sx={fieldSx}
                                type="date"
                                size="small"
                                label={<FormattedMessage id="start_date" />}
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => setActiveFrom(e.target.value)}
                                value={activeFrom}
                            />
                            <TextField
                                sx={fieldSx}
                                type="date"
                                size="small"
                                label={<FormattedMessage id="end_date" />}
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => setActiveUntil(e.target.value)}
                                value={activeUntil}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel id="status-label"><FormattedMessage id="ads_state" /></InputLabel>
                                <Select
                                    labelId="status-label"
                                    value={isActive}
                                    label={<FormattedMessage id="ads_state" />}
                                    onChange={(e) => setIsActive(e.target.value)}
                                >
                                    <MenuItem value="1">
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <CircleIcon sx={{ color: "#16a34a", fontSize: 14 }} />
                                            <FormattedMessage id="active" />
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="0">
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <CircleIcon sx={{ color: "#64748b", fontSize: 14 }} />
                                            <FormattedMessage id="stopped" />
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </SectionCard>

                    <SectionCard
                        icon={<ImageOutlinedIcon />}
                        title={isAr ? "صورة الإعلان" : "Banner image"}
                        subtitle={isAr ? "تظهر في كاروسيل الصفحة الرئيسية للطالب" : "Displayed in the student homepage carousel"}
                        theme={theme}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                                minHeight: 200,
                                borderRadius: 3,
                                overflow: "hidden",
                                border: "2px dashed",
                                borderColor: imagePreview ? "#86efac" : "#cbd5e1",
                                bgcolor: theme.palette.mode === "dark" ? "background.paper" : "#fff",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                            }}
                        >
                            {imagePreview ? (
                                <Box
                                    component="img"
                                    src={imagePreview}
                                    alt=""
                                    sx={{ width: "100%", maxHeight: 260, objectFit: "cover" }}
                                />
                            ) : (
                                <Box sx={{ py: 4, textAlign: "center" }}>
                                    <Box component="img" src={UploadImage} alt="" sx={{ width: 56, mb: 1 }} />
                                    <Typography variant="body1" fontWeight={700} sx={{ color: "#0D358C" }}>
                                        <FormattedMessage id="add_image" />
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                                        {isAr ? "يفضل صورة عريضة 16:9 للكاروسيل" : "Wide 16:9 image recommended for carousel"}
                                    </Typography>
                                </Box>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    setImage(file || "");
                                    if (file) {
                                        setImagePreview(URL.createObjectURL(file));
                                    } else {
                                        setImagePreview("");
                                    }
                                }}
                            />
                        </Box>
                        {imagePreview ? (
                            <Chip
                                label={isAr ? "تم اختيار الصورة" : "Image selected"}
                                size="small"
                                sx={{ mt: 1.5, bgcolor: "#f0fdf4", color: "#166534", fontWeight: 700 }}
                            />
                        ) : null}
                    </SectionCard>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end" }}>
                    <Button
                        variant="outlined"
                        onClick={() => { resetInputs(); onClickClose(); }}
                        disabled={sendWait}
                        sx={{ minWidth: 120 }}
                    >
                        <FormattedMessage id="cancel" />
                    </Button>
                    <Button
                        onClick={addAds}
                        variant="contained"
                        disabled={sendWait}
                        startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                        sx={{
                            minWidth: 180,
                            bgcolor: "#0D358C",
                            fontWeight: 700,
                            boxShadow: "0 4px 14px rgba(13,53,140,0.35)",
                            "&:hover": { bgcolor: "#1d4ed8" },
                        }}
                    >
                        {sendWait ? (isAr ? "جاري الحفظ..." : "Saving...") : <FormattedMessage id="add" />}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default AddAds;
